/*global process*/
import callApi, { CallApiParams } from "@web-monorepo/infra/callApi";
import createWaitableSaga from "@web-monorepo/infra/createWaitableSaga";
import { APIResponseError, isApiDownStatus, isSuccessStatus } from "@web-monorepo/infra/responseHandlers";
import stableStringify from "fast-stable-stringify";
import curry from "lodash/curry";
import identity from "lodash/identity";
import isFunction from "lodash/isFunction";
import ms from "ms";
import type { AnyAction } from "redux";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { Response } from "superagent";

type GetEndAction = (...args: unknown[]) => AnyAction | AnyAction[];

let lastServerRetryTimes = [0, 0];
const SERVER_ERROR_RETRY_THROTTLE = 10 * 1000;

/**
  Saga for making requests to the API. Assumes a `flux-standard-action` type action.

  Relevant action `meta` properties:
    - `refresh`: Force a refresh, regardless of cache or fetched status.

  @param {string} startActionType - Action to trigger this saga
  @param {Function} getRequestParams - Given the action payload, return a payload to
    be passed to `callApi`.
  @param {string|Function} getEndAction - Given the api response, dispatch this
    action.  If a function is provided, it will be called with the api response and
    the payload of the init action.  If a string representing an action type is provided,
    a standard function is generated: on 2xx response, parse the body as JSON, and dispatch
    an action like {type, payload: {...originalPayload, response}}, and throw in other cases.

  @param {Object} params - Fetch configuration
    @param {Function} params.preventConcurrent - If a second request with an identical
      payload to one in progress arrives, should it be skipped
    @param {Function} params.hasFetched - Selector that determines whether data has been fetched
    @param {number} params.cacheTime - Time (as a string or in ms) after which data is
      considered stale and refetched

  @returns {Function} - A saga for fetching this data.
*/
const LEGAL_PARAMS = ["preventConcurrent", "hasFetched", "cacheTime"];
export default function createApiSaga(
  startActionType: string,
  getRequestParams: (payload: Record<string, unknown>, state: Record<string, unknown>) => CallApiParams,
  getEndAction: string | GetEndAction,
  params: {
    cacheTime?: string | number;
    hasFetched?: (state: Record<string, unknown>, payload: Record<string, unknown>) => boolean;
    preventConcurrent?: boolean;
  } = {},
) {
  if (Config.nodeEnv === "development" || Config.nodeEnv === "test") {
    Object.keys(params).forEach((param) => {
      if (!LEGAL_PARAMS.includes(param)) {
        throw new Error(`Illegal parameter to \`createApiSaga\`: ${param}`);
      }
    });
  }

  let getEndActionLocal: GetEndAction = isFunction(getEndAction) ? getEndAction : ignoreResponse;

  // If an API call has no expected error cases, we can pass a string instead of a function
  // for `getEndAction` and make a standard function.  See docs above.
  if (typeof getEndAction === "string") {
    getEndActionLocal = actionFromResponse(getEndAction);
  } else if (getEndAction == null) {
    getEndActionLocal = ignoreResponse;
  }

  // Allow specification of cache time as a human-readable string.
  if (typeof params.cacheTime === "string") params.cacheTime = ms(params.cacheTime);

  const fetchesInProgress: Record<string, number> = {};
  const fetchTimes: Record<string, number> = {};

  // eslint-disable-next-line complexity
  const makeCall = createWaitableSaga(function* (startAction: {
    meta: Record<string, unknown>;
    payload: Record<string, unknown>;
  }) {
    const meta = startAction.meta || {};
    const payload = startAction.payload || {};
    const stringifiedPayload = stableStringify(payload);

    // If a `hasFetched` function is specified, get its value
    let hasFetched = false;
    if (params.hasFetched) {
      hasFetched = yield select(params.hasFetched, payload);
    }

    // If a `cacheTime` parameter is specified, fetch if we have not fetched recently.
    let cacheExpired = false;
    if (params.cacheTime) {
      const now = Date.now();
      const lastFetch = fetchTimes[stringifiedPayload] != null ? fetchTimes[stringifiedPayload] : -Infinity;
      cacheExpired = now - lastFetch > Number(params.cacheTime);
    }

    // If `preventConcurrent` parameter is specified, fetch only if no identical
    // request is in progress.
    const isAlreadyFetching = params.preventConcurrent && fetchesInProgress[stringifiedPayload] > 0;

    const shouldFetchData = meta.refresh || ((cacheExpired || !hasFetched) && !isAlreadyFetching);
    if (!shouldFetchData) return;

    fetchesInProgress[stringifiedPayload] = (fetchesInProgress[stringifiedPayload] || 0) + 1;
    fetchTimes[stringifiedPayload] = Date.now();

    try {
      const state: Record<string, unknown> = yield select(identity);
      const params: CallApiParams = yield call(getRequestParams, payload, state);
      const response: Response = yield call(callApi, params);
      const endAction: AnyAction | AnyAction[] = yield call(getEndActionLocal, response, payload);
      if (Array.isArray(endAction)) {
        // Handling the case of propagating the "fetcher" metadata
        // "else" do a saga "put" for each endAction as normal.
        let actions;
        if (startAction.meta && startAction.meta.fetcher) {
          actions = endAction
            .filter((action) => action != null)
            .map((_action) => {
              // Fetcher: We want to propagate the meta we have from the initial action to the end action.
              if (!_action.meta) _action.meta = {};
              _action.meta.fetcher = startAction.meta.fetcher;
              return _action;
            });
        } else {
          actions = endAction.filter((action) => action != null);
        }

        for (const action of actions) {
          yield put(action);
        }
      } else if (endAction != null) {
        // Fetcher: We want to propagate the meta we have from the initial action to the end action.
        if (startAction.meta && startAction.meta.fetcher) {
          if (!endAction.meta) endAction.meta = {};
          endAction.meta.fetcher = startAction.meta.fetcher;
        }
        yield put(endAction);
      }
      fetchesInProgress[stringifiedPayload] -= 1;
    } catch (error) {
      fetchesInProgress[stringifiedPayload] -= 1;

      // Special handling for API Down errors - retry with a throttle.
      const { response } = error;
      if (response && isApiDownStatus(response.status)) {
        const now = Date.now();
        const shouldRetry = now - lastServerRetryTimes[0] > SERVER_ERROR_RETRY_THROTTLE;
        if (shouldRetry) {
          lastServerRetryTimes = lastServerRetryTimes.concat(now).slice(-2);
          yield* makeCall(startAction);
          return;
        }
      }

      // Rethrow error so it can be caught by the error pods middleware
      throw error;
    }
  });

  return function* apiCallSaga() {
    yield takeEvery(startActionType, makeCall);
  };
}

export const actionFromResponse = curry(
  (successActionType: string, httpResponse: Response, originalPayload: Record<string, unknown>) => {
    if (httpResponse.status === 204) {
      return {
        type: successActionType,
        payload: originalPayload,
      };
    } else if (isSuccessStatus(httpResponse.status)) {
      return {
        type: successActionType,
        payload: { ...originalPayload, response: httpResponse.body },
      };
    } else {
      throw new APIResponseError(httpResponse);
    }
  },
);

// throw if there's an error, but don't dispatch any actions.
export const ignoreResponse = (httpResponse: Response) => {
  if (isSuccessStatus(httpResponse.status)) {
    return [];
  }
  throw new APIResponseError(httpResponse);
};
